import { BaseViewStore, BaasicDropdownStore } from 'core/stores';
import { applicationContext, QueryUtility } from 'core/utils';
import { action, observable, runInAction } from 'mobx';
import _ from 'lodash';
import { FacilityOrderTemplateNewFlowEditViewStore } from "application/facility-order-new-flow/stores";
import { FacilityOrderTemplateService, FacilityOrderService, FacilityOrderStatusService } from "application/facility-order/services";
import { ItemCategoryService } from 'application/product/services';
import { LookupService } from "common/services";
import { PickTicketHeaderService } from "application/pick-ticket/services";
import { ScheduleService} from 'application/schedule/services';
import { LocalPatientService } from "application/patient/services";

@applicationContext
class FacilityOrderNewFlowPreviewViewStore extends BaseViewStore {
    @observable accessor facilityOrderTemplateStores;
    @observable accessor selectedStatusAbrv;
    @observable accessor fetchingTemplates = false;
    @observable accessor itemCategories = [];
    @observable accessor disabled = false;
    @observable accessor disabledSendOrder = false;
    @observable accessor isStatusBarVisible = true;
    @observable accessor disabledStatus = false;
    @observable accessor pickTicketHeaderStatus;
    @observable accessor facilityOrder = null;
    @observable accessor notification;
    tabItems = [{ abrv: 'in-progress', name: 'In Progress' }, { abrv: 'new-order', name: 'New Orders' }, { abrv: 'change', name: 'Changes' },
    { abrv: 'no-change', name: 'No Changes' }, { abrv: 'discharge', name: 'Discharges' }];

    constructor(rootStore) {
        super(rootStore);

        this.id = rootStore.routerStore.routerState.params.id;
        this.isAdmin = rootStore.userStore.user.roles.includes("Administrators");

        this.facilityOrderService = new FacilityOrderService(rootStore.application.baasic.apiClient);
        this.facilityOrderTemplateService = new FacilityOrderTemplateService(rootStore.application.baasic.apiClient);
        this.facilityOrderStatusService = new FacilityOrderStatusService(rootStore.application.baasic.apiClient);
        this.itemCategoryService = new ItemCategoryService(rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);
        this.scheduleService = new ScheduleService(rootStore.application.baasic.apiClient);

        this.itemCategoryStore = new BaasicDropdownStore({
            multi: true, filterable: false, initFetch: false
        }, {
            fetchFunc: async (term) => {
                const response = await this.itemCategoryService.find({
                    searchQuery: term,
                    rpp: 100
                });
                let filteredCategories = response.data.item.filter(i => this.facilityOrder.isEnteralWeek ? i.abrv === "enteral" : (i.abrv === "enteral" || i.abrv === "respiratory" || i.abrv === "urological"
                    || i.abrv === "ostomy" || i.abrv === "wound-care"));
                return filteredCategories;
            },
        });

        this.queryUtility = new QueryUtility(this.rootStore, async filter => {
            await this.fetchFacilityOrderTemplates(filter);
        }, {});

        this.fetch([
            this.initialize()
        ]);
    }

    @action.bound
    async initialize() {
        await this.initializeFacilityOrder();
        await this.initializeNewFlowStatusLookups();
        await this.setStatus(this.selectedStatusAbrv);

        const typeResponse = await this.scheduleService.getTypes();
            this.orderType = _.find(typeResponse.item, {abrv: 'ordering'});
    }

    @action.bound
    async fetchFacilityOrderTemplatesByPatient(patientId) {
        var itemCategories = _.map(this.itemCategories, (i => { return i.id }));
        let params = {
            ...this.queryUtility.plainFilter,
            pageSize: 10000,
            facilityOrderId: this.id,
            patientId: patientId,
            embed: 'FacilityOrderTemplateItems,Patient,SubCategory,ItemCategory,ImportReference,NewFlowStatus,NewFlowInsuranceStatus,NewFlowUSIntakeStatus,ReviewUSIntakeValidations,FacilityOrderTemplateMissingDocuments,OrderingDoctor,ClinicalStatus,IsFacilityOrderTemplateItemInitial',
            orderBy: "SubCategory",
            orderDirection: "asc",
            itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : (itemCategories && itemCategories.length > 0) ? itemCategories : undefined,
            masterPatientId: this.facilityOrder.isEnteralWeek ? this.masterPatientId : undefined
        }

        return (await this.facilityOrderTemplateService.find(params)).data.item;
    }

    @action.bound
    async initializeFacilityOrder() {
        const response = await this.facilityOrderService.get(this.id, { embed: 'Facility,Facility.FacilityAreaOfCares,FacilityOrderStatuses,ImportHeaderStatus,FacilityOrderPatients' });
        const facilityOrder = response.data;
        const isInventoryStepAllowed = (await this.facilityOrderService.isInventoryStepAllowed(this.id)).data;
        const localPatientService = new LocalPatientService(this.rootStore.application.baasic.apiClient);
        const masterPatient = (await localPatientService.getByPatientKey(facilityOrder.facility.masterPatientId, { fields: 'id' })).data;
        runInAction(() => {
            this.facilityOrder = facilityOrder;
            this.isInventoryStepAllowed = isInventoryStepAllowed;
            this.pickTicketHeaderId = facilityOrder.pickTicketHeaderId;
            this.masterPatientId = masterPatient ? masterPatient.id : undefined;
        });
        await this.initializePickTicketHeader();
        await this.itemCategoryStore.filterAsync();
        this.rootStore.titleStore.setTitle(`Initial Intake - ${facilityOrder.facility.name}`);
        runInAction(() => {
            this.facilityOrderStatus = facilityOrder.facilityOrderStatuses.find(i => i.status.abrv === 'initial-intake');
            this.isInitialIntakeStatus = facilityOrder.importHeaderStatus && facilityOrder.importHeaderStatus.abrv === "initial-intake";
            this.usIntakeStatus = facilityOrder.facilityOrderStatuses.find(x => x.status && x.status.abrv === "us-intake");
            this.disabled = !(this.facilityOrderStatus && this.facilityOrderStatus.startedOn && !this.facilityOrderStatus.completedOn) || this.isDisabled();
            this.disabledSendOrder = !this.isAdmin && facilityOrder && facilityOrder.sendOrder;
        });
    }

    @action.bound
    async initializePickTicketHeader() {
        if (!this.pickTicketHeaderId) {
            this.pickTicketHeaderId = (await this.facilityOrderService.get(this.id, { fields: 'PickTicketHeaderId' })).data.pickTicketHeaderId;
        }
        if (this.pickTicketHeaderId) {
            const pickTicketHeaderService = new PickTicketHeaderService(this.rootStore.application.baasic.apiClient);
            const pickTicketHeader = (await pickTicketHeaderService.get(this.pickTicketHeaderId, { embed: "pickTicketHeaderStatus" })).data;
            this.pickTicketHeaderStatus = pickTicketHeader && pickTicketHeader.pickTicketHeaderStatus && pickTicketHeader.pickTicketHeaderStatus.abrv;
        }
    }

    @action.bound
    isDisabled() {
        return this.usIntakeStatus && !(this.facilityOrderStatus && this.facilityOrderStatus.completedOn);
    }

    @action.bound
    async initializeNewFlowStatusLookups() {
        const lookups = await this.lookupService.getLookups("NewFlowStatus,ItemCategory,SubCategory,ImportAction,ImportDetailStatus,ImportHeaderStatus");
        const enteralItemCategoryId = _.find(lookups.itemCategory, s => {
            return s.abrv === 'enteral';
        }).id;
        let queryParams = this.rootStore.routerStore.routerState.queryParams;
        const selectedStatusAbrv = _.find(lookups.newFlowStatus, s => {
            return queryParams && queryParams.newFlowStatusId ? s.id === queryParams.newFlowStatusId : s.abrv === 'in-progress';
        }).abrv;
        runInAction(() => {
            this.lookups = lookups;
            this.enteralItemCategoryId = enteralItemCategoryId;
            this.selectedStatusAbrv = selectedStatusAbrv;
        });
    }

    @action.bound
    async setStatus(statusAbrv) {
        this.selectedStatusAbrv = statusAbrv;
        const statusId = _.find(this.lookups.newFlowStatus, s => {
            return s.abrv === statusAbrv;
        }).id;

        this.queryUtility.filter.set('newFlowStatusId', statusId);
        this.queryUtility.fetch();
    }

    @action.bound
    async checkIsInitialReviewCompleted() {
        if (!this.facilityOrderTemplateStores.some(i => i.items) && (this.facilityOrderStatus && !this.facilityOrderStatus.completedOn)) {
            await this.initializeFacilityOrder();
            await this.setStatus(this.selectedStatusAbrv);
        }
    }

    @action.bound
    async fetchFacilityOrderTemplates(filter) {
        runInAction(() => {
            this.fetchingTemplates = true;
        });
        var itemCategories = _.map(this.itemCategories, (i => { return i.id }));
        let params = {
            ...filter,
            pageSize: 10000,
            facilityOrderId: this.id,
            embed: 'FacilityOrderTemplateItems,Patient,SubCategory,ItemCategory,ImportReference,NewFlowStatus,IsFacilityOrderTemplateItemInitial,ClinicalStatus,ReviewUSIntakeValidations',
            orderBy: "SubCategory",
            orderDirection: "asc",
            itemCategoryIds: this.facilityOrder.isEnteralWeek ? [this.enteralItemCategoryId] : (itemCategories && itemCategories.length > 0) ? itemCategories : undefined,
            masterPatientId: this.facilityOrder.isEnteralWeek ? this.masterPatientId : undefined
        }
        
        let facilityOrderTemplates = (await this.facilityOrderTemplateService.find(params)).data.item;
        let grouped = _.groupBy(facilityOrderTemplates, 'patientId');

        let facilityOrderTemplatesGroupByPatient = [];
        _.each(grouped, (val, key) => {
            facilityOrderTemplatesGroupByPatient.push({ id: key, item: val, facilityOrder: this.facilityOrder, facilityOrderPatient: this.facilityOrder.facilityOrderPatients.find(x => x.patientId == key), lookups: this.lookups, selectedStatusAbrv: this.selectedStatusAbrv, selectedItemCategories: itemCategories, checkIsInitialReviewCompleted: () => this.checkIsInitialReviewCompleted(), refresh: () => this.queryUtility.fetch(), fetchTemplatesByPatientId: () => this.fetchFacilityOrderTemplatesByPatient(key), pageViewStore: this });
        });

        const facilityOrderTemplateStores = facilityOrderTemplatesGroupByPatient.map(item =>
            new FacilityOrderTemplateNewFlowEditViewStore(this.rootStore, item)
        );
        runInAction(() => {
            this.facilityOrderTemplateStores = facilityOrderTemplateStores;
            this.fetchingTemplates = false;
        });
    }

    @action.bound
    async goToFacilityPreview() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.facility.preview').pattern.replace(":id", this.facilityOrder.facilityId)}`);
    }

    @action.bound
    async goToFacilityCalendar() {
        window.open(`${this.rootStore.routerStore.findRoute('master.app.main.schedule.calendar').pattern}?facilityId=${this.facilityOrder.facilityId}&calendarTypeId=${this.orderType.id}`);
    }

    @action.bound
    async goToCreatePatient() {
        this.rootStore.routerStore.goTo('master.app.main.facility-order.facility-order-new-flow.create-patient', {
            id: this.facilityOrder.id
        });
    }

    @action.bound
    async downloadFacilityOrder() {
        const downloadUrl = `${(await this.facilityOrderTemplateService.getDownloadNewFlowUrl({ ...this.queryUtility.plainFilter, facilityOrderId: this.facilityOrder.id }))}&contentDisposition=attachment&token=${this.rootStore.authStore.token.tokenUrl
            }&ttype=${this.rootStore.authStore.token.type}`
        window.open(downloadUrl, "_blank")
    }

    @action.bound
    async filterByCategory(selected) {
        this.itemCategories = selected.value;
        this.queryUtility.fetch();
    }

    @action.bound
    async setInitialIntakeStarted() {
        if (this.facilityOrderStatus)
            return;

        this.disabledStatus = true;
        try {
            const status = this.lookups.importHeaderStatus.find(i => i.abrv === 'initial-intake');
            var response = await this.facilityOrderStatusService.create({ facilityOrderId: this.facilityOrder.id, statusId: status.id, startedOn: new Date(), startedByUserId: this.rootStore.userStore.user.id });
            if (response.data) {
                this.facilityOrderStatus = response.data;
                this.disabled = false;
            }
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        await this.getFacilityOrderStatus();
        this.disabledStatus = false;
    }

    @action.bound
    async setInitialIntakeCompleted() {
        if (!this.facilityOrderStatus || this.facilityOrderStatus.completedOn)
            return;

        this.disabledStatus = true;
        try {
            const request = { ...this.facilityOrderStatus, completedOn: new Date(), completedByUserId: this.rootStore.userStore.user.id };
            await this.facilityOrderStatusService.update(request);
            this.facilityOrderStatus = request;
            this.disabled = true;
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }
        await this.getFacilityOrderStatus();
        this.disabledStatus = false;

        await this.facilityOrderTemplateService.updateFacilityOrderTemplateIntakeStatuses({ facilityOrderId: this.facilityOrder.id });
    }

    @action.bound
    async restartInitialIntakeReview() {
        this.disabledStatus = true;
        try {
            const request = { ...this.facilityOrderStatus, completedOn: null, completedByUserId: null };
            await this.facilityOrderStatusService.update(request);
            this.facilityOrderStatus = request;
            this.disabled = false;
        }
        catch (err) {
            this.rootStore.notificationStore.error("The facility order status has already been updated. Please reload the page.");
        }

        await this.getFacilityOrderStatus();
        this.disabledStatus = false;
    }

    @action.bound
    async getFacilityOrderStatus() {
        var statusesResponse = await this.facilityOrderStatusService.find({ facilityOrderId: this.facilityOrder.id, embed: "Status,StartedByUser,CompletedByUser" });
        if (statusesResponse.data) {
            this.facilityOrder.facilityOrderStatuses = statusesResponse.data.item;
        }

        const response = await this.facilityOrderService.get(this.id, { embed: 'ImportHeaderStatus', fields: "ImportHeaderStatus" });
        if (response.data) {
            this.facilityOrder.importHeaderStatus = response.data.importHeaderStatus;
            this.isInitialIntakeStatus = this.facilityOrder.importHeaderStatus && this.facilityOrder.importHeaderStatus.abrv === "initial-intake";
        }

        this.changeStatusBarVisibility();
        this.changeStatusBarVisibility();
    }

    @action.bound
    changeStatusBarVisibility() {
        this.isStatusBarVisible = !this.isStatusBarVisible;
    }

    @action.bound
    signalRAction(message) {
        if (message.text && (message.userId != this.rootStore.userStore.user.id && this.facilityOrderTemplateStores && this.facilityOrderTemplateStores.some(fo => fo && ((fo.items && fo.items.some(i => i && i.id == message.id)) || fo.id == message.id)))) {
            this.notification = message.text;
        }
        else if (message.table == "PTH") {
            this.initializePickTicketHeader();
        }
        else if (message.column == "SendOrder") {
            this.facilityOrder.sendOrder = true;
            this.disabledSendOrder = !this.isAdmin;
        }
    }
}

export default FacilityOrderNewFlowPreviewViewStore;
